import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { loginRequest, mfaStepUpAuthority } from "./msalConfig";
import { getDeviceFingerprint, getDeviceName, isMobileDevice } from "../utils/deviceFingerprint";

const STAY_LOGGED_IN_KEY = "beekeeper.stay_logged_in";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [stayLoggedIn, setStayLoggedInState] = useState(
    localStorage.getItem(STAY_LOGGED_IN_KEY) === "true"
  );

  const account = accounts[0] || null;

  /**
   * A 401 the user cannot fix by signing in again: no app.Users row, or a
   * misconfigured deployment. Held as state so App.jsx can render a
   * terminal explanation instead of the app bouncing to the sign-out page
   * (#166). Shape: { kind: "not_provisioned" | "misconfigured", message }.
   */
  const [authBlock, setAuthBlock] = useState(null);

  const login = useCallback(() => instance.loginRedirect(loginRequest), [instance]);
  const logout = useCallback(() => instance.logoutRedirect(), [instance]);

  // Cleared on an explicit sign-out so a user who signs in as someone else
  // is not still looking at the previous account's dead end.
  const reportAuthBlock = useCallback((kind, message) => setAuthBlock({ kind, message }), []);
  const clearAuthBlock = useCallback(() => setAuthBlock(null), []);

  /** Redirects to the MFA step-up B2C policy; call this when the API
   * returns 401 mfa_required (see services/apiClient.js). */
  const stepUpMfa = useCallback(
    () => instance.loginRedirect({ ...loginRequest, authority: mfaStepUpAuthority }),
    [instance]
  );

  /* The absolute session cap (#457). prompt:"login" is the point: an
     ordinary redirect would hit a live IdP cookie and come straight back
     with the SAME auth_time, so the server would refuse again and the
     user would bounce in a loop. This forces a genuine re-authentication,
     which is the only thing that moves that clock. */
  const reauthenticate = useCallback(
    () => instance.loginRedirect({ ...loginRequest, prompt: "login" }),
    [instance]
  );

  const getAccessToken = useCallback(async () => {
    if (!account) throw new Error("Not authenticated");
    try {
      const result = await instance.acquireTokenSilent({ ...loginRequest, account });
      return result.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect(loginRequest);
        return null; // page is navigating away
      }
      throw err;
    }
  }, [instance, account]);

  const setStayLoggedIn = useCallback((value) => {
    localStorage.setItem(STAY_LOGGED_IN_KEY, String(value));
    setStayLoggedInState(value);
  }, []);

  const value = useMemo(
    () => ({
      isAuthenticated,
      account,
      login,
      logout,
      stepUpMfa,
      reauthenticate,
      getAccessToken,
      stayLoggedIn,
      setStayLoggedIn,
      deviceFingerprint: getDeviceFingerprint(),
      deviceName: getDeviceName(),
      sessionType: isMobileDevice() ? "mobile" : "web",
      authBlock,
      reportAuthBlock,
      clearAuthBlock,
    }),
    [
      isAuthenticated, account, login, logout, stepUpMfa, reauthenticate, getAccessToken,
      stayLoggedIn, setStayLoggedIn, authBlock, reportAuthBlock, clearAuthBlock,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
